import React from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, MessageSquare } from "lucide-react";
import SEO from "@/components/SEO";
import {
  MarketingHeader, MarketingFooter, MarketingBackdrop,
} from "@/components/MarketingShell";

export default function ContactPage() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "url": "https://www.powerbuilt.sx/contact",
    "about": { "@id": "https://www.powerbuilt.sx/#org" },
  };
  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home",    "item": "https://www.powerbuilt.sx/" },
      { "@type": "ListItem", "position": 2, "name": "Contact", "item": "https://www.powerbuilt.sx/contact" },
    ],
  };
  return (
    <MarketingBackdrop>
      <SEO
        path="/contact"
        title="Contact PowerBuilt — Solar, Electrical &amp; Maintenance in Sint Maarten"
        description="Get in touch with PowerBuilt in Sint Maarten for solar, electrical and system maintenance quotes, service requests and consultations."
        jsonLd={{ "@context": "https://schema.org", "@graph": [jsonLd, breadcrumb] }}
      />
      <MarketingHeader />

      <main className="relative z-10 max-w-3xl mx-auto px-6 pt-8 md:pt-16 pb-16">
        <nav aria-label="Breadcrumb" className="text-xs text-white/50 mb-6">
          <Link to="/" className="hover:text-white">Home</Link> <span className="mx-1">/</span> <span className="text-white/70">Contact</span>
        </nav>

        <p className="overline text-[#C9A356] mb-4">Contact</p>
        <h1 className="text-4xl sm:text-5xl font-heading font-medium tracking-tight leading-[1.1]">
          Let&#39;s scope your project.
        </h1>
        <p className="mt-6 text-lg text-slate-300 leading-relaxed max-w-xl">
          Tell us what you&#39;re trying to power &mdash; a home, a business, a boat, a single circuit. PowerBuilt will come back with the honest options.
        </p>

        <div className="mt-12 grid md:grid-cols-3 gap-5">
          <a href="mailto:hello@powerbuilt.sx" className="group rounded-2xl border border-white/10 bg-white/[0.03] p-7 hover:bg-white/[0.05] transition hover:border-[#C9A356]/50 block" data-testid="contact-email">
            <Mail className="w-6 h-6 text-[#C9A356] mb-4" />
            <h2 className="font-heading text-lg mb-1">Email</h2>
            <p className="text-sm text-slate-400 break-all">hello@powerbuilt.sx</p>
          </a>
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-7">
            <MapPin className="w-6 h-6 text-[#C9A356] mb-4" />
            <h2 className="font-heading text-lg mb-1">Workshop</h2>
            <p className="text-sm text-slate-400">Cole Bay, Sint Maarten</p>
          </div>
          <Link to="/register" className="group rounded-2xl border border-[#C9A356]/40 bg-[#C9A356]/8 p-7 hover:bg-[#C9A356]/12 transition block" data-testid="contact-portal">
            <MessageSquare className="w-6 h-6 text-[#C9A356] mb-4" />
            <h2 className="font-heading text-lg mb-1">Customer portal</h2>
            <p className="text-sm text-slate-300">Open a PowerBuilt account to raise quotes and service requests.</p>
          </Link>
        </div>

        <section className="mt-16 border-t border-white/10 pt-8">
          <p className="overline text-[#C9A356] mb-3">What we can help with</p>
          <ul className="space-y-2 text-slate-300 list-disc pl-5 marker:text-[#C9A356]">
            <li><Link to="/solar" className="underline decoration-[#C9A356]/50 hover:decoration-[#C9A356]">Solar power &amp; battery storage</Link> quotes for homes, businesses and marine.</li>
            <li><Link to="/electrical" className="underline decoration-[#C9A356]/50 hover:decoration-[#C9A356]">Electrical installations &amp; upgrades</Link> — panel upgrades, dedicated circuits, EV-ready wiring.</li>
            <li><Link to="/maintenance" className="underline decoration-[#C9A356]/50 hover:decoration-[#C9A356]">Ongoing system maintenance</Link> and service tickets for existing installs.</li>
          </ul>
        </section>
      </main>

      <MarketingFooter />
    </MarketingBackdrop>
  );
}
